import {
  Box,
  Collapse,
  Container,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from "@material-ui/core";
import React, { useEffect, useRef, useState } from "react";
import axios from "../../axios";
import NameField from "../NameField/NameField";
import numeral from "numeral";
import sort from "../../utils/sort";
import { currentCurrency } from "../../features/currencySlice";
import { useSelector } from "react-redux";
import cloneDeep from "lodash/cloneDeep";
import CoinRowExpand from "../CoinRowDetails/CoinRowExpand";
import LoadingTableRows from "./LoadingTableRows";

const useStyles = makeStyles({
  table: {},
});

const useTableStyles = makeStyles({
  tableRow: {
    cursor: "pointer",
    transition: "background-color 0.2s ease-in-out",
    "&:hover": {
      backgroundColor: "#F0F0F0",
    },
  },

  same: {
    transition: "background-color 0.2s ease-in-out",
    backgroundColor: "#ffffff",
  },
  increased: {
    transition: "background-color 0.2s ease-in-out",
    backgroundColor: "rgba(24, 198, 131, 0.19)",
  },
  decreased: {
    transition: "background-color 0.2s ease-in-out",
    backgroundColor: "rgba(244, 67, 54, 0.19)",
  },
  percentRed: {
    color: "rgb(244, 67, 54)",
  },
  percentGreen: {
    color: "rgb(24, 198, 131)",
  },
});

function Row({ row, curSymbol }) {
  const [open, setOpen] = useState(false);

  const classes = useTableStyles();

  const [bgColor, setBGColor] = useState(classes.same);

  useEffect(() => {
    row.changeLabel === "same"
      ? setBGColor(classes.same)
      : row.changeLabel === "increased"
      ? setBGColor(classes.increased)
      : setBGColor(classes.decreased);
  }, [row]);
  useEffect(() => {
    if (bgColor !== "same") {
      setTimeout(() => setBGColor("same"), 1000);
    }
  }, [bgColor]);

  return (
    <>
      <TableRow
        onClick={() => setOpen(!open)}
        className={[bgColor, classes.tableRow]}
      >
        <TableCell size="small" align="right">
          {row.rank}
        </TableCell>
        <TableCell size="small" align="left">
          <NameField name={row.name} symbol={row.symbol} />
        </TableCell>
        <TableCell size="small" align="right">
          {row.priceUsd > 1
            ? curSymbol + numeral(row.priceUsd).format("0,0.00")
            : curSymbol + numeral(row.priceUsd).format("0.00000000")}
        </TableCell>
        <TableCell size="small" align="right">
          {curSymbol + numeral(row.marketCapUsd).format("(0.00a)")}
        </TableCell>
        <TableCell size="small" align="right">
          {isNaN(row.vwap24Hr)
            ? "-"
            : row.vwap24Hr > 1
            ? curSymbol + numeral(row.vwap24Hr).format("0,0.00")
            : curSymbol + numeral(row.vwap24Hr).format("0.00000000")}
        </TableCell>
        <TableCell size="small" align="right">
          {numeral(row.supply).format("(0.00a)")}
        </TableCell>
        <TableCell size="small" align="right">
          {curSymbol + numeral(row.volumeUsd24Hr).format("(0.00a)")}
        </TableCell>
        <TableCell
          size="small"
          align="right"
          className={
            row.changePercent24Hr >= 0
              ? classes.percentGreen
              : classes.percentRed
          }
        >
          {numeral(row.changePercent24Hr).format("0.00") + "%"}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
          <Collapse in={open} unmountOnExit>
            <CoinRowExpand coinData={row} />
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

function LeaderboardTable() {
  const [origData, setOrigData] = useState([]);
  const [prevData, setPrevData] = useState([]);
  const [loading, setLoading] = useState(true);

  const [curData, setCurData] = useState([]);
  const [sortedData, setSortedData] = useState([]);

  const currentCurSelected = useSelector(currentCurrency);

  const [tblHeader, setTblHeader] = useState([
    { id: "rank", label: "Rank" },
    { id: "name", label: "Name" },
    { id: "priceUsd", label: "Price" },
    { id: "marketCapUsd", label: "Market Cap" },
    { id: "vwap24Hr", label: "VWAP (24Hr)" },
    { id: "supply", label: "Supply" },
    { id: "volumeUsd24Hr", label: "Volume(24 Hr)" },
    { id: "changePercent24Hr", label: "Change(24 hr)" },
  ]);

  const [order, setOrder] = useState();
  const [orderBy, setOrderBy] = useState();

  const handleSortClick = (cellId) => {
    const isAsc = orderBy === cellId && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(cellId);
  };

  useEffect(() => {
    setInterval(() => {
      axios
        .get("/assets")
        .then((res) => {
          setOrigData((pre) => {
            setPrevData(pre);
            return res.data.data;
          });
        })
        .catch((err) => console.log(err));
    }, 3000);
  }, []);
  useEffect(() => {
    setCurData(cloneDeep(origData));
  }, [origData]);
  useEffect(() => {
    curData.map((d, index) => {
      if (prevData[index] === undefined) {
        d.changeLabel = "same";
      } else {
        let diff = 0;
        if (d?.priceUsd > 1) {
          diff =
            parseFloat(parseFloat(d?.priceUsd).toFixed(2)) -
            parseFloat(parseFloat(prevData[index]?.priceUsd).toFixed(2));
        } else {
          diff =
            parseFloat(parseFloat(d?.priceUsd).toFixed(8)) -
            parseFloat(parseFloat(prevData[index]?.priceUsd).toFixed(8));
        }

        d.changeLabel =
          diff === 0 ? "same" : diff > 0 ? "increased" : "decreased";
      }

      d.priceUsd = d.priceUsd / currentCurSelected.rateUsd;
      d.marketCapUsd = d.marketCapUsd / currentCurSelected.rateUsd;
      d.volumeUsd24Hr = d.volumeUsd24Hr / currentCurSelected.rateUsd;
      d.vwap24Hr = d.vwap24Hr / currentCurSelected.rateUsd;

      curData[index] = d;
      if (loading === true) setLoading(false);
    });
    //console.log(curData[0]);

    setSortedData(sort(curData, order, orderBy, "coin"));
    //console.log("current currency changed!!", currentCurSelected);
  }, [currentCurSelected, curData, order, orderBy]);
  const classes = useStyles();
  return (
    <TableContainer component={Paper} elevation={10}>
      <Table style={{ widht: 600 }}>
        <TableHead>
          <TableRow>
            {tblHeader.map((header) => {
              return (
                <TableCell
                  size="small"
                  align={header.id === "name" ? "left" : "right"}
                  key={header.id}
                  sortDirection={header.id === orderBy ? order : false}
                >
                  <TableSortLabel
                    active={orderBy === header.id}
                    direction={orderBy === header.id ? order : "asc"}
                    onClick={() => {
                      handleSortClick(header.id);
                    }}
                  >
                    {header.label}
                  </TableSortLabel>
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <LoadingTableRows numberOfRow={15} numberOfColumn={8} />
          ) : (
            sortedData.map((row) => {
              //console.log(row);
              const curSymbol =
                currentCurSelected.currencySymbol !== null
                  ? currentCurSelected.currencySymbol
                  : "";

              return <Row row={row} curSymbol={curSymbol} key={row.rank} />;
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default LeaderboardTable;
